// src/components/dashboard/EmailTable.tsx

import React from 'react';
import { AlertCircle, Clock, Mail, RefreshCw, Trash2, XCircle } from 'lucide-react';
import { Email } from '../../types';
import { STAGE_TONE_CLASS, describeStage } from '../../types/stage';

interface EmailTableProps {
  emails: Email[];
  selectedEmails: string[];
  allMatchingSelected: boolean;
  onSelectEmail: (emailId: string) => void;
  onSelectAll: () => void;
  onDeleteEmail: (emailId: string) => void;
  onRetryEmail: (emailId: string) => void;
  onViewDetails: (email: Email) => void;
  isLoading?: boolean;
  emptyHint?: string;
}

const PRIORITIES: Record<number, { label: string; color: string }> = {
  1: { label: 'Critical', color: 'bg-red-100 text-red-800' },
  2: { label: 'High', color: 'bg-orange-100 text-orange-800' },
  3: { label: 'Normal', color: 'bg-blue-100 text-blue-800' },
  4: { label: 'Low', color: 'bg-gray-100 text-gray-800' },
  5: { label: 'Bulk', color: 'bg-gray-50 text-gray-600' },
};

const relative = (date: string | Date | null) => {
  if (!date) return '—';
  const d = new Date(date);
  const diffMs = Date.now() - d.getTime();
  const past = diffMs >= 0;
  const mins = Math.floor(Math.abs(diffMs) / 60000);

  let text: string;
  if (mins < 1) text = 'just now';
  else if (mins < 60) text = `${mins}m`;
  else if (mins < 1440) text = `${Math.floor(mins / 60)}h`;
  else text = `${Math.floor(mins / 1440)}d`;

  if (mins < 1) return text;
  return past ? `${text} ago` : `in ${text}`;
};

const absolute = (date: string | Date | null) =>
  date ? new Date(date).toLocaleString() : '';

const EmailTable: React.FC<EmailTableProps> = ({
  emails,
  selectedEmails,
  allMatchingSelected,
  onSelectEmail,
  onSelectAll,
  onDeleteEmail,
  onRetryEmail,
  onViewDetails,
  isLoading = false,
  emptyHint,
}) => {
  const allOnPageSelected =
    emails.length > 0 && emails.every((e) => selectedEmails.includes(e.emailId));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="animate-spin h-7 w-7 text-gray-400" />
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <div className="text-center py-16">
        <Mail className="mx-auto h-10 w-10 text-gray-300" />
        <h3 className="mt-3 text-sm font-medium text-gray-900">Nothing here</h3>
        <p className="mt-1 text-sm text-gray-500">
          {emptyHint ?? 'No emails match the current filter.'}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-4 py-3 text-left w-10">
              <input
                type="checkbox"
                checked={allOnPageSelected || allMatchingSelected}
                onChange={onSelectAll}
                aria-label="Select all on this page"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Stage
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Recipient
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Subject
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Reference
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Priority
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Attempts
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Queued
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {emails.map((email) => {
            const isSelected = allMatchingSelected || selectedEmails.includes(email.emailId);
            const stage = describeStage(email.stage, email.deliveryStatus);

            // The detail worth surfacing on the row itself rather than hiding in a
            // modal: when something failed, why it failed is the whole point.
            const problem =
              email.smtpResponseMessage ||
              email.deliveryStatusMessage ||
              email.lastErrorMessage;
            const showProblem =
              !!problem && (stage.tone === 'bad' || stage.tone === 'warn');

            const rowTint =
              stage.tone === 'bad'
                ? 'bg-red-50/60 hover:bg-red-50'
                : email.isHeld
                ? 'bg-amber-50/60 hover:bg-amber-50'
                : 'hover:bg-gray-50';

            return (
              <tr key={email.emailId} className={rowTint}>
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={allMatchingSelected}
                    onChange={() => onSelectEmail(email.emailId)}
                    aria-label={`Select ${email.toAddress}`}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-60"
                  />
                </td>

                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex flex-col gap-1 items-start">
                    <span
                      title={stage.description}
                      className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${STAGE_TONE_CLASS[stage.tone]}`}
                    >
                      {stage.label}
                    </span>
                    {email.isHeld && (
                      <span
                        title={
                          email.isPaused
                            ? 'Paused by an operator.'
                            : email.nextRetryAt
                            ? `Waiting for a retry ${relative(email.nextRetryAt)}.`
                            : `Scheduled for ${absolute(email.processAfter)}.`
                        }
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border border-dashed border-amber-300 text-amber-700"
                      >
                        <Clock size={11} />
                        {email.isPaused ? 'Paused' : `Held ${relative(email.nextRetryAt ?? email.processAfter)}`}
                      </span>
                    )}
                  </div>
                </td>

                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">{email.toAddress}</div>
                  <div className="text-xs text-gray-500">from {email.fromAddress}</div>
                </td>

                <td className="px-4 py-3 max-w-xs">
                  <div className="text-sm text-gray-900 truncate" title={email.subject || ''}>
                    {email.subject || <span className="text-gray-400">(no subject)</span>}
                  </div>
                  {showProblem && (
                    <div
                      className="mt-1 flex items-start gap-1 text-xs text-red-700"
                      title={problem ?? ''}
                    >
                      <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                      <span className="truncate">
                        {email.smtpStatusCode ? `${email.smtpStatusCode}: ` : ''}
                        {problem}
                      </span>
                    </div>
                  )}
                </td>

                <td className="px-4 py-3 whitespace-nowrap">
                  {email.clientReference ? (
                    <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                      {email.clientReference}
                    </code>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>

                <td className="px-4 py-3 whitespace-nowrap">
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      (PRIORITIES[email.priority] ?? PRIORITIES[3]).color
                    }`}
                  >
                    {(PRIORITIES[email.priority] ?? PRIORITIES[3]).label}
                  </span>
                </td>

                <td className="px-4 py-3 whitespace-nowrap text-sm tabular-nums">
                  <span
                    className={
                      email.attemptCount >= email.maxAttempts
                        ? 'text-red-700 font-medium'
                        : 'text-gray-600'
                    }
                  >
                    {email.attemptCount}/{email.maxAttempts}
                  </span>
                  {email.nextRetryAt && (
                    <div className="text-xs text-gray-500">
                      next {relative(email.nextRetryAt)}
                    </div>
                  )}
                </td>

                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  <span title={absolute(email.queuedAt)}>{relative(email.queuedAt)}</span>
                </td>

                <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={() => onViewDetails(email)}
                      className="text-blue-600 hover:text-blue-900 font-medium"
                    >
                      View
                    </button>
                    {email.smtpStatus === 'Failed' && (
                      <button
                        onClick={() => onRetryEmail(email.emailId)}
                        className="text-green-600 hover:text-green-900"
                        title="Put this back in the queue"
                      >
                        <RefreshCw size={15} />
                      </button>
                    )}
                    {email.stage === 'Queued' && (
                      <button
                        onClick={() => onDeleteEmail(email.emailId)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete this email"
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                    {email.stage === 'Bounced' && (
                      <XCircle size={15} className="text-red-400" aria-hidden="true" />
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default EmailTable;
